// 已保存账号「导出账号密码」图片:数据模型(纯,可测) + Canvas 绘制(本地,明文不出浏览器)。
import type { SavedAccount } from '@dogdisk/shared'

export const EXPORT_DOT_PALETTE = ['#19caad', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'] as const

export type ExportRow = { name: string; account: string; password: string; color: string }
export type ExportModel = { dateStr: string; rows: ExportRow[] }

export function buildExportModel(accounts: SavedAccount[], dateStr: string): ExportModel {
  const rows = accounts.map((a, i) => ({
    name: a.username,
    account: a.username,
    password: a.password ? a.password : '(未保存密码)',
    color: EXPORT_DOT_PALETTE[i % EXPORT_DOT_PALETTE.length]!,
  }))
  return { dateStr, rows }
}

// 绘制对齐 ZRvFX:外层 #F6F7F8 圆角16 padding18 + #E5E7EB 描边;品牌头(青色 logo chip +
// 「账号备份」+ 日期)+ 分隔线;每账号白卡(圆角12 padding14):圆点+用户名 / 账号 / 密码。
// 在 scale=3 下绘制保证清晰。jsdom 无 2D 上下文 → getContext 返回 null 时静默返回。
export function drawAccountExport(canvas: HTMLCanvasElement, model: ExportModel): void {
  // jsdom 无 canvas 后端时 getContext 会抛/返 null;整体兜底,绝不让绘制异常冒泡。
  let ctx: CanvasRenderingContext2D | null = null
  try {
    ctx = canvas.getContext('2d')
  } catch {
    return
  }
  if (!ctx) return

  const S = 3
  const W = 342
  const PAD = 18
  const CARD_PAD = 14
  const HEADER_H = 26
  const DIVIDER_GAP = 14
  const CARD_GAP = 14
  const NAME_H = 26
  const ROW_H = 22
  const CARD_INNER = NAME_H + ROW_H + ROW_H
  const CARD_H = CARD_PAD + CARD_INNER + CARD_PAD
  const innerTop = PAD + HEADER_H + DIVIDER_GAP
  const cardsH = model.rows.length * CARD_H + Math.max(0, model.rows.length - 1) * CARD_GAP
  const H = innerTop + cardsH + PAD

  canvas.width = W * S
  canvas.height = H * S
  canvas.style.width = `${W}px`
  canvas.style.height = `${H}px`
  ctx.scale(S, S)

  const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
  }

  // 外层底
  ctx.fillStyle = '#F6F7F8'
  roundRect(0.5, 0.5, W - 1, H - 1, 16)
  ctx.fill()
  ctx.lineWidth = 1
  ctx.strokeStyle = '#E5E7EB'
  ctx.stroke()

  // 品牌头:logo chip + 标题 + 日期
  ctx.fillStyle = '#19caad'
  roundRect(PAD, PAD + 3, 18, 18, 5)
  ctx.fill()
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#1A1A1A'
  ctx.font = '700 15px "DM Sans", sans-serif'
  ctx.textAlign = 'left'
  ctx.fillText('账号备份', PAD + 26, PAD + 12)
  ctx.fillStyle = '#9CA3AF'
  ctx.font = '400 12px "DM Sans", sans-serif'
  ctx.textAlign = 'right'
  ctx.fillText(model.dateStr, W - PAD, PAD + 12)

  // 分隔线
  ctx.strokeStyle = '#E5E7EB'
  ctx.beginPath()
  ctx.moveTo(PAD, PAD + HEADER_H)
  ctx.lineTo(W - PAD, PAD + HEADER_H)
  ctx.stroke()

  // 账号卡
  model.rows.forEach((row, i) => {
    const cardY = innerTop + i * (CARD_H + CARD_GAP)
    ctx.fillStyle = '#FFFFFF'
    roundRect(PAD, cardY, W - PAD * 2, CARD_H, 12)
    ctx.fill()

    const cx = PAD + CARD_PAD
    let cy = cardY + CARD_PAD
    // 名称行:圆点 + 用户名
    ctx.fillStyle = row.color
    ctx.beginPath()
    ctx.arc(cx + 11, cy + 11, 11, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#1A1A1A'
    ctx.font = '700 15px "DM Sans", sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText(row.name, cx + 30, cy + 11)
    cy += NAME_H
    // 账号行
    ctx.fillStyle = '#9CA3AF'
    ctx.font = '600 13px "DM Sans", sans-serif'
    ctx.fillText('账号', cx, cy + 11)
    ctx.fillStyle = '#1A1A1A'
    ctx.font = '400 14px "DM Sans", sans-serif'
    ctx.fillText(row.account, cx + 44, cy + 11)
    cy += ROW_H
    // 密码行
    ctx.fillStyle = '#9CA3AF'
    ctx.font = '600 13px "DM Sans", sans-serif'
    ctx.fillText('密码', cx, cy + 11)
    ctx.fillStyle = '#1A1A1A'
    ctx.font = '600 14px "DM Sans", sans-serif'
    ctx.fillText(row.password, cx + 44, cy + 11)
  })
}
